export default class PluginManager {
    /**
     * Creates an instance of PluginManager.
     * @param {Object} config
     * @param {Array} config.plugins - List of plugin instances
     * @param {Object} config.dataset - Reference to lookup module dataset
     */
    constructor({ plugins = [], dataset = {} }) {
        this.dataset = dataset;
        this.queryPlugins = plugins.filter(plugin => plugin && typeof plugin.query === 'function');
        this.postRenderPlugins = plugins.filter(plugin => plugin && typeof plugin.postRender === 'function');
    }

    /**
     * Calls the query hook on each plugin in turn
     * @param {String} query - The current user input
     * @param {Function} resolve - Resolves the encompassing promise
     * @param {Function} reject - Rejects the encompassing promise (will result in console error)
     * @param {Object} localResults - A reference to any local results for the query
     */
    query(query, resolve, reject, localResults) {
        this.localResults = localResults;
        const queryPromise = {
            resolve: resolve,
            reject: reject,
        };

        if (this.queryPlugins.length > 0) {
            this.triggerQueryPlugins(query, queryPromise, this.queryPlugins);
        }
        else {
            resolve();
        }
    }

    /**
     * Calls Plugin.query on current plugin and exposes promise-like API
     * @param {String} query - The current user input
     * @param {Object} queryPromise - Wrapper around the encompassing promise
     * @param {Array} plugins - List of plugins that have a `query` function
     * @param {Number} [count = 0] - Tracks index of current plugin
     */
    triggerQueryPlugins(query, queryPromise, plugins, count = 0) {
        const currentPlugin = plugins[count];
        const hasNextPlugin = count < plugins.length - 1;

        const done = response => {
            queryPromise.resolve({
                suggestions: response,
            });
        };

        const skip = (opts = {}) => {
            const { suppressError } = opts;
            if (hasNextPlugin) {
                this.triggerQueryPlugins(query, queryPromise, plugins, count + 1);
            }
            else if (suppressError !== undefined) {
                queryPromise.resolve({ suppressError: suppressError });
            }
            else {
                queryPromise.resolve();
            }
        };

        const error = response => {
            queryPromise.resolve({
                error: response,
            });
        };

        currentPlugin.query(query, done, skip, error, this.localResults, this.dataset);
    }

    /**
     * Calls plugin.postRender on each compatible plugin
     * @param {Object} els - Contains references to lookup module HTMLElements
     */
    postRender(els) {
        this.postRenderPlugins.forEach(plugin => {
            plugin.postRender(els);
        });
    }
}
